<template>
<div>
  <vx-card ref="listCard" :title="$t('MRMR.Variances.Upload.Title')"  class="mb-3" title-color="black" :loading="isLoading" :loading-text="$t('Loading')">
    <ValidationObserver ref="observer">
      <vs-row v-if="!isFileUploaded || (uploadResults.validationResults && uploadResults.validationResults.validations.some(_ => _.success === false))">
      <vs-col vs-w="2">
        <form-label>{{ $t('MRMR.Statement.Period') }}</form-label>
        <ValidationProvider :name="$t('MRMR.Statement.Period')" rules="required" v-slot="{ errors }">
          <v-select
            label="name"
            append-to-body
            :options="sortedPeriods"
            v-model="mrmrStatementPeriodId"
            :reduce="p => p.id"
            class="w-full"
          />
          <validation-message>{{ errors[0] }}</validation-message>
        </ValidationProvider>
      </vs-col>
      <vs-col vs-w="6" class="ml-3">
        <form-label>{{ $t('MRMR.Variances.Upload.UploadFile') }}</form-label><br/>
        <form-label>{{ file ? file.name : '' }}</form-label>
        <input type="file" class="inputfile" ref="fileInput" id="testResults" @change="handleFileChange" accept=".xlsx,.xlsm" :disabled="isLoading"/>
        <label for="testResults" class="mt-1" :class="file && file.name ? 'ml-3':''">{{ $t('MRMR.Variances.Upload.BrowseFile') }}</label>
        <div class="text-danger">{{ fileExtensionValidationMessage }}</div>
      </vs-col>
    </vs-row>
    <vs-row v-if="!isFileUploaded|| (uploadResults.validationResults && uploadResults.validationResults.validations.some(_ => _.success === false))" class="pt-4">
      <vs-col vs-w="3">
        <vs-button class="mr-6" @click="upload()" :color="'primary'" icon-pack="feather" icon="icon-upload" :disabled="isLoading || !file || !file.name">{{ $t('MRMR.Variances.Upload.UploadFile') }}</vs-button>
      </vs-col>
      <vs-col vs-w="5">
        <vs-button type="border" color="danger" @click="cancel()">{{ $t('MRMR.Variances.Upload.ReturnToList') }}</vs-button>
      </vs-col>
    </vs-row>
    <vs-row v-if="isFileUploaded" class="mt-4">
      <vs-col vs-w="9">
        <div v-if="uploadResults.validationResults && uploadResults.validationResults.validations.some(_ => _.success === false)">
          <h5>{{ $t('MRMR.Variances.Upload.ValidationsFailed') }}</h5>
          <label class="mb-3">{{ $t('MRMR.Variances.Upload.ValidationsFailedText') }}</label>
          <div 
            v-for="sheet, idx in uploadResults.validationResults.validations" :key="idx">
            <table class="table table-bordered table-striped">
              <thead>
                <tr>
                  <th colspan="2">{{ sheet.pipeline }} : {{ sheet.message }} {{ sheet.pass_count}}/{{ sheet.total_checks }} has passed</th>
                </tr>
                <tr>
                  <th>{{ $t('MRMR.Variances.Upload.ValidationRule') }}</th>
                  <th>{{ $t('Success') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="value, key in sheet.results" :key="key">
                    <td>{{value.definitionName}}</td>
                    <td class="text-center">
                      <feather-icon v-if="!value.hasFailures" icon="CheckIcon" class="p-4 mb-4 bg-primary rounded-full text-white shadow" svg-classes="h-6 w-6"  @click.stop="toggleDataSidebar(true)" />
                      <feather-icon v-if="value.hasFailures" icon="XIcon" class="p-4 mb-4 bg-danger rounded-full text-white shadow" svg-classes="h-6 w-6"  @click.stop="toggleDataSidebar(true)" />
                    </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-if="uploadResults.validationResults && uploadResults.validationResults.validations.every(_ => _.success === true)">
          <h5>{{ $t('MRMR.Variances.Upload.UploadSuccess') }}</h5>
          <div class="box">
            <label>{{ $t('MRMR.Variances.Upload.UploadSuccessText') }}</label>
            <feather-icon style="display:flex;" icon="ListIcon" class=" push p-2 mb-4 bg-secondary cursor-pointer rounded-full text-white shadow" svg-classes="h-6 w-6"  @click.stop="showLegend=!showLegend" />
          </div>
          <table class="table table-unbordered" width="100%">
            <thead>
              <tr>
                <th>{{ $t('MRMR.Variances.VarianceEntries.Name') }}</th>
                <th style="text-align: right" class="tonnage-cell">{{ $t('MRMR.Variances.VarianceEntries.Tonnage') }}</th>
                <th style="text-align: right" class="grade-cell">{{ $t('MRMR.Variances.VarianceEntries.Grade') }}</th>
                <th style="text-align: right" class="ounce-cell">{{ $t('MRMR.Variances.VarianceEntries.Ounce') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td v-if="getProvenProbableEntries().length > 0" colspan="4"><h5>{{ $t('MRMR.Variances.VarianceEntries.ProvenProbable') }}</h5></td>
              </tr>
              <tr v-for="value, key in getProvenProbableEntries()" :key="`proven_${key}`" :class="getVarianceCategoryClassByName(value)">
                <td>{{value.name}}</td>
                <td style="text-align: right">{{value.tonnage.toLocaleString(undefined, {minimumFractionDigits:3, maximumFractionDigits:3}) }}</td>
                <td style="text-align: right">{{value.grade.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2}) }}</td>
                <td style="text-align: right">{{value.ounce.toLocaleString(undefined, {minimumFractionDigits:3, maximumFractionDigits:3}) }}</td>
              </tr>
              <tr class="header-row">
                <td v-if="getMeasuredIndicatedEntries().length > 0" colspan="4" class="header-cell"><h5>{{ $t('MRMR.Variances.VarianceEntries.MeasuredIndicated') }}</h5></td>
              </tr>
              <tr v-for="value, key in getMeasuredIndicatedEntries()" :key="`measured_${key}`" :class="getVarianceCategoryClassByName(value)">
                <td>{{value.name}}</td>
                <td style="text-align: right">{{value.tonnage.toLocaleString(undefined, {minimumFractionDigits:3, maximumFractionDigits:3}) }}</td>
                <td style="text-align: right">{{value.grade.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2}) }}</td>
                <td style="text-align: right">{{value.ounce.toLocaleString(undefined, {minimumFractionDigits:3, maximumFractionDigits:3}) }}</td>
              </tr>
              <tr class="header-row">
                <td v-if="getInferredEntries().length > 0" colspan="4" class="header-cell"><h5>{{ $t('MRMR.Variances.VarianceEntries.Inferred') }}</h5></td>
              </tr>
              <tr v-for="value, key in getInferredEntries()" :key="`inferred_${key}`" :class="getVarianceCategoryClassByName(value)">
                <td>{{value.name}}</td>
                <td style="text-align: right">{{value.tonnage.toLocaleString(undefined, {minimumFractionDigits:3, maximumFractionDigits:3}) }}</td>
                <td style="text-align: right">{{value.grade.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2}) }}</td>
                <td style="text-align: right">{{value.ounce.toLocaleString(undefined, {minimumFractionDigits:3, maximumFractionDigits:3}) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </vs-col>
    </vs-row>
    <vs-row>
      <vs-col vs-w="9" class="text-right">
        <vs-button 
          class="mt-3"
          v-if="uploadResults.validationResults && uploadResults.validationResults.validations.every(_ => _.success === true)"
           @click="submitNewFile()" type="border" color="danger">{{ $t('MRMR.Variances.Upload.SubmitNewFile') }}</vs-button>
        <vs-button 
          class="mt-3 ml-3"
          v-if="uploadResults.validationResults && uploadResults.validationResults.validations.every(_ => _.success === true)"
           @click="confirmSubmission()" :color="'primary'">{{ $t('Import') }}</vs-button>
      </vs-col>
    </vs-row>
  </ValidationObserver>
  </vx-card>
    <vs-popup :active.sync="showLegend" class="holamundo" :title="$t('MRMR.Variances.Upload.LegendTitle')">
          <table class="table table-unbordered" width="100%">
            <tbody>
              <tr v-for="category in getMRMRVarianceCategoryEnum()" :key="category" :class="getVarianceCategoryClass(category)">
                <td>{{ $t(`MRMR.Variances.Upload.MRMRVarianceCategoryEnum.${category}`) }}</td>
              </tr>
            </tbody>
          </table>
    </vs-popup>
</div>
</template>
<script>

import {
} from "../../../shared/components";
import varianceService from "../services/varianceService";
import vSelect from 'vue-select';
import { ValidationMessage, FormLabel } from "@/app/shared/components";
import moduleMRMR from "../store/moduleMRMR.js";
import MRMRVarianceCategoryEnum from "./MRMRVarianceCategoryEnum.js"
import ResourceTypeEnum from "../ResourceTypeEnum";
export default {
  /* eslint-disable vue/no-unused-components */
  components: {
    vSelect,
    ValidationMessage,
    FormLabel
  },
  data() {
    return {
      uploadResults: {},
      isFileUploaded: false,
      file: {},
      isLoading: false,
      showError: false,
      mrmrStatementPeriodId: null,
      selectedProjectId: null,
      fileExtensionValidationMessage: '',
      showLegend: false,
      previousStatement: {}
    }
  },
  computed: {
    periods() {
      return this.$store.state.mrmr.periods.filter(_ => _.createdStatementsCount > 0);
    },
    sortedPeriods() {
      const list = [...this.periods];
      return list.sort((a, b) => { return (a.periodDate < b.periodDate) ? -1 : 1 });
    }
  },

  created() {
    if (!moduleMRMR.isRegistered) {
      this.$store.registerModule("mrmr", moduleMRMR);
      moduleMRMR.isRegistered = true;
    }
    this.selectedProjectId = Number(this.$store.state.projectId);
  },
  mounted() {
    if (this.$store.state.mrmr && this.$store.state.mrmr.periods.length === 0) {
      this.$vs.loading();
      this.$store.dispatch("mrmr/fetchPeriodListItems").finally(() => {
        this.$vs.loading.close();
      });
    }
  },
  methods: {
    validateFile() {
      if (this.file.name !== undefined) {
        const allowedExtensions = ['xlsx', 'xlsm'];
        const fileExtension = this.file.name.split('.').pop().toLowerCase();

        if (fileExtension && allowedExtensions.includes(fileExtension)) {
          this.fileExtensionValidationMessage = '';
        } else {
          this.fileExtensionValidationMessage = this.$t('MRMR.Variances.Upload.BadFileExtension');
          this.file = {};
        }
      }
    },
    getMRMRVarianceCategoryEnum() {
      return MRMRVarianceCategoryEnum;
    },
    async upload() {
      this.validateFile();
      this.$refs.observer.validate().then(async result => {
        if (!result || this.fileExtensionValidationMessage !== '') {
          return;
        }

        this.showError = false;
        this.uploadResults = {};

        try {
          document.body.style.cursor = 'progress';
          this.isLoading = true;
          if (this.file.name !== undefined) {
            const fileContent = await this.file.arrayBuffer();
            this.uploadResults = await varianceService.upload(this.selectedProjectId, this.mrmrStatementPeriodId, this.file.name, this.file.type, fileContent, this)
            if (this.uploadResults.entries && this.uploadResults.entries.length > 0) {
              this.previousStatement = await varianceService.findPreviousStatement(this.uploadResults.entries[0].uploadId, this);
              this.addProvenProbablePreviousStatementsValues();
              this.addMeasuredIndicatedPreviousStatementsValues();
              this.addInferredPreviousStatementsValues();
            }
            this.addProvenProbableTotals();
            this.addMeasuredIndicatedTotals();
            this.addInferredTotals();
            this.isFileUploaded = true;
          } else {
            this.showError = true;
            this.file = {};
          }
        } 
        catch (e)
        {
          console.error(e);
        }
        finally
        {
          document.body.style.cursor = 'default'
          this.isLoading = false;
        }
      });

    },
    cancel() {
      this.$router.push({ name: "geo-variances" });
    },
    handleFileChange() {
      this.file = this.$refs.fileInput.files[0];
      this.showError = false;
    },
    getVarianceCategoryClassByName(item) {
      switch (item.category) {
      case 'Depletion from Mineral Reserves':
      case 'Depletion from Mineral Resources':
      case 'Net change in stockpile balance':
        return this.getVarianceCategoryClass(0);
      case 'Interpretation / Drilling':
        return this.getVarianceCategoryClass(3);
      case 'Mining approach':
        return this.getVarianceCategoryClass(4);
      case 'New estimation parameters':
        return this.getVarianceCategoryClass(5);
      case 'New COG':
        return this.getVarianceCategoryClass(6);
      case 'Classification Revision (other than drilling)':
        return this.getVarianceCategoryClass(7);
      case 'Study and Acquisition':
        return this.getVarianceCategoryClass(8);
      }
      return '';
    },
    getVarianceCategoryClass(item) {
      return `variance-category${item}`;
    },
    getProvenProbableEntries() {
      if (!this.uploadResults.entries) {
        return [];
      }
      return this.uploadResults.entries.filter(_ => _.mrmrResourceType === "Proven & Probable Reserves");
    },
    getMeasuredIndicatedEntries() {
      if (!this.uploadResults.entries) {
        return [];
      }
      return this.uploadResults.entries.filter(_ => _.mrmrResourceType === "Measured & Indicated Resources");
    },
    getInferredEntries() {
      if (!this.uploadResults.entries) {
        return [];
      }
      return this.uploadResults.entries.filter(_ => _.mrmrResourceType === "Inferred Resources");
    },
    addProvenProbablePreviousStatementsValues() {
      const stats = this.previousStatement.stats.filter(_ => _.resourceType === ResourceTypeEnum.Probable || _.resourceType === ResourceTypeEnum.Proven);
      this.uploadResults.entries.splice(0, 0, {
        name: this.$t('MRMR.Variances.Upload.ReservesAsEstimated', {x: this.previousStatement.periodDate.split('T')[0]}),
        mrmrResourceType: "Proven & Probable Reserves",
        tonnage: stats.reduce((acc, cur) => acc + cur.tonnage, 0),
        grade: stats.reduce((acc, cur) => acc + cur.goldGrade, 0),
        ounce: stats.reduce((acc, cur) => acc + cur.auOz, 0)
      });
    },
    addMeasuredIndicatedPreviousStatementsValues() {
      const stats = this.previousStatement.stats.filter(_ => _.resourceType === ResourceTypeEnum.Measured || _.resourceType === ResourceTypeEnum.Indicated);
      this.uploadResults.entries.splice(0, 0, {
        name: this.$t('MRMR.Variances.Upload.ResourceAsEstimated', {x: this.previousStatement.periodDate.split('T')[0]}),
        mrmrResourceType: "Measured & Indicated Resources",
        tonnage: stats.reduce((acc, cur) => acc + cur.tonnage, 0),
        grade: stats.reduce((acc, cur) => acc + cur.goldGrade, 0),
        ounce: stats.reduce((acc, cur) => acc + cur.auOz, 0)
      });
    },
    addInferredPreviousStatementsValues() {
      const stats = this.previousStatement.stats.filter(_ => _.resourceType === ResourceTypeEnum.Inferred);
      this.uploadResults.entries.splice(0, 0, {
        name: this.$t('MRMR.Variances.Upload.ResourceAsEstimated', {x: this.previousStatement.periodDate.split('T')[0]}),
        mrmrResourceType: "Inferred Resources",
        tonnage: stats.reduce((acc, cur) => acc + cur.tonnage, 0),
        grade: stats.reduce((acc, cur) => acc + cur.goldGrade, 0),
        ounce: stats.reduce((acc, cur) => acc + cur.auOz, 0)
      });
    },
    addProvenProbableTotals() {
      const entries = this.getProvenProbableEntries();
      if (entries.length === 0) {
        return { tonnage: 0, ounce: 0, grade: 0 };
      }
      const tonnage = entries.reduce((sum, entry) => sum + entry.tonnage, 0);
      const ounce = entries.reduce((sum, entry) => sum + entry.ounce, 0);
      const grade = ounce * 31.1035 / tonnage;
      this.uploadResults.entries.push({
        name: "Total",
        tonnage,
        ounce,
        grade,
        mrmrResourceType: "Proven & Probable Reserves"
      });
    },
    addMeasuredIndicatedTotals() {
      const entries = this.getMeasuredIndicatedEntries();
      if (entries.length === 0) {
        return { tonnage: 0, ounce: 0, grade: 0 };
      }
      const tonnage = entries.reduce((sum, entry) => sum + entry.tonnage, 0);
      const ounce = entries.reduce((sum, entry) => sum + entry.ounce, 0);
      const grade = ounce * 31.1035 / tonnage;
      this.uploadResults.entries.push({
        name: "Total",
        tonnage,
        ounce,
        grade,
        mrmrResourceType: "Measured & Indicated Resources"
      });
    },
    addInferredTotals() {
      const entries = this.getInferredEntries();
      if (entries.length === 0) {
        return { tonnage: 0, ounce: 0, grade: 0 };
      }
      const tonnage = entries.reduce((sum, entry) => sum + entry.tonnage, 0);
      const ounce = entries.reduce((sum, entry) => sum + entry.ounce, 0);
      const grade = ounce * 31.1035 / tonnage;
      this.uploadResults.entries.push({
        name: "Total",
        tonnage,
        ounce,
        grade,
        mrmrResourceType: "Inferred Resources"
      });
    },
    confirmSubmission() {
      const uploadId = this.uploadResults.entries.find(_ => _.uploadId).uploadId;
      varianceService.confirmSubmission(uploadId, this)
        .then(() => {
          this.$vs.notify({
            color: "success",
            title: this.$t("Success"),
            text: this.$t("MRMR.Variances.Upload.ImportedSuccessfully")
          });
          this.$router.push({ name: "geo-variances" });
        })
        .catch((error) => {
          console.error(error);
        });
    },
    submitNewFile() {
      this.isFileUploaded = false;
      this.file = {};
      this.uploadResults = {};
    }
  }
};
</script>

<style>
.inputfile {
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  z-index: -1;
}
.inputfile + label {
  padding:4px;
  padding-left:10px;
  padding-right:10px;
  color: white;
  background-color: #009cde;
  display: inline-block;
  border: 1px solid #009cde
}

.inputfile:focus + label,
.inputfile + label:hover {
  background-color: #26abe3;
  border: 1px solid #1aa6e1
}
.variance-category0 {
  background-color: #000000;
  color: #ffffff;
}
.variance-category1 {
  background-color: #000000;
  color: #ffffff;
}
.variance-category2 {
  background-color: #000000;
  color: #ffffff;
}
.variance-category3 {
  background-color: #E9EDF7;
  color: #000000;
}
.variance-category4 {
  background-color: #D6EDBD;
  color: #000000;
}
.variance-category5 {
  background-color: #FFFFAF;
  color: #000000;
}
.variance-category6 {
  background-color: #FFE48F;
  color: #000000;
}
.variance-category7 {
  background-color: #44B3E1;
  color: #000000;
}
.variance-category8 {
  background-color: #FF99FF;
  color: #000000;
}
.header-row {
  height: 50px;
}
.header-cell {
  vertical-align: bottom;
}
.grade-cell {
  width: 100px;
}
.ounce-cell {
  width: 150px;
}
.tonnage-cell {
  width: 150px;
}
.table-unbordered {
  border-spacing: unset;
}
.box {
  display:flex;
}

.push {
  margin-left: auto;
}
</style>
